import { useEffect, useState } from 'react';

interface ComplianceGaugeProps {
    percentage: number;
    title: string;
    subtitle?: string;
    size?: number;
}

export default function ComplianceGauge({
    percentage,
    title,
    subtitle,
    size = 160,
}: ComplianceGaugeProps) {
    const [animatedPercentage, setAnimatedPercentage] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            setAnimatedPercentage(percentage);
        }, 100);
        return () => clearTimeout(timer);
    }, [percentage]);

    const radius = (size - 16) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (animatedPercentage / 100) * circumference;

    const getColor = () => {
        if (percentage >= 90) return '#10b981'; // Safety green
        if (percentage >= 70) return '#f59e0b'; // Alert amber
        return '#ef4444'; // Red
    };

    const getGradientId = () => {
        if (percentage >= 90) return 'safetyGradient';
        if (percentage >= 70) return 'alertGradient';
        return 'dangerGradient';
    };

    return (
        <div className="card-glass p-8 flex flex-col items-center animate-scaleIn">
            <div className="relative" style={{ width: size, height: size }}>
                {/* Background Circle */}
                <svg className="transform -rotate-90" width={size} height={size}>
                    <defs>
                        <linearGradient id="safetyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#10b981" />
                            <stop offset="100%" stopColor="#059669" />
                        </linearGradient>
                        <linearGradient id="alertGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#f59e0b" />
                            <stop offset="100%" stopColor="#d97706" />
                        </linearGradient>
                        <linearGradient id="dangerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#ef4444" />
                            <stop offset="100%" stopColor="#dc2626" />
                        </linearGradient>
                    </defs>

                    {/* Background track */}
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        stroke="#e2e8f0"
                        strokeWidth="8"
                        fill="none"
                    />

                    {/* Progress circle */}
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        stroke={`url(#${getGradientId()})`}
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                        className="transition-all duration-1000 ease-out"
                        style={{ filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))' }}
                    />
                </svg>

                {/* Center text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="text-4xl font-display font-bold text-charcoal-950">
                        {Math.round(animatedPercentage)}%
                    </div>
                    <div className="text-xs text-steel-600 mt-1">Compliant</div>
                </div>

                {/* Glow effect */}
                <div
                    className="absolute inset-0 rounded-full opacity-20 blur-xl animate-glow"
                    style={{ backgroundColor: getColor() }}
                />
            </div>

            {/* Labels */}
            <div className="mt-6 text-center">
                <h4 className="font-display font-semibold text-charcoal-900">
                    {title}
                </h4>
                {subtitle && (
                    <p className="text-sm text-steel-600 mt-1">{subtitle}</p>
                )}
            </div>
        </div>
    );
}
